/**
 * Settings → Proyectos — everything about one project, in one place.
 *
 * Before this screen existed, administering a project meant visiting five
 * sections that each opened with the same question: Repositories, Plane,
 * Proyectos vinculados, Comandos locales and Entornos all began by asking
 * "which project?", and answering it in one told the others nothing. The work
 * is per project, so the *project* is the noun the screen is organised around:
 * pick it once at the top, then move between tabs that all speak about it.
 *
 * ── What did not move ─────────────────────────────────────────────────────
 *
 * Two halves of the old sections are installation-wide and deliberately stayed
 * behind in Settings → Plane: the connection to the Plane instance, and each
 * person's own Plane credential. Repointing the instance changes every project
 * at once, so it is not a fact about the project selected here — and putting it
 * under a project picker would suggest otherwise.
 *
 * ── Which tabs appear ─────────────────────────────────────────────────────
 *
 * Per project, from the viewer's grants on *that* project — which is why the
 * nav item carries no `requiredPermission`: `p.edit` is a project permission
 * and `settingsNavigation` can only express global ones. Someone who edits one
 * project of ten sees the full set on that one and the bare Etiquetas tab on
 * the rest, rather than a screen that is either all-or-nothing.
 *
 * The chosen project is remembered in `localStorage` because this screen is
 * where a person returns to keep working on the same repository; the tab is
 * not, since arriving at Repositorio because that is where you were three days
 * ago is disorienting.
 */
import type { ProjectId, Tag, TagId } from "@taskin-code/contracts";
import { useEffect, useMemo, useState } from "react";

import { useAuthStore, useHasProjectPermission } from "../../auth/authStore";
import { useStore } from "../../store";
import { TagsClient } from "../../tags/TagsClient";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "../ui/select";
import { EnvironmentsSection } from "./EnvironmentsSection";
import { LinkedProjectsSection } from "./LinkedProjectsSection";
import { LocalCommandsSection } from "./LocalCommandsSection";
import { PlaneProjectSection } from "./PlaneSection";
import { QaAutomationSection } from "./QaAutomationSection";
import { ProjectBaseBranchSection } from "./ProjectBaseBranchSection";
import { ProjectRepoSection } from "./ProjectRepoSection";
import { TagPicker } from "./TagPicker";

const STORAGE_KEY = "taskin.settings.projects.selected";

type TabId = "tags" | "repo" | "plane" | "linked" | "commands" | "environments" | "qa-automation";

const readStoredProject = (): string | null => {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // A browser with site data blocked still gets a working screen, it just
    // opens on the first project every time.
    return null;
  }
};

export function ProjectsSection() {
  const projects = useStore((state) => state.projects);
  const [projectId, setProjectId] = useState<ProjectId | null>(null);
  const [tab, setTab] = useState<TabId>("tags");

  // Restore only once the projects are known, and only if the remembered one
  // still exists — a project can be removed between visits.
  useEffect(() => {
    if (projectId !== null || projects.length === 0) return;
    const stored = readStoredProject();
    const remembered = projects.find((project) => project.id === stored);
    setProjectId(((remembered ?? projects[0])!.id as ProjectId) ?? null);
  }, [projects, projectId]);

  useEffect(() => {
    if (projectId === null) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, projectId);
    } catch {
      /* Remembering is a convenience, not a requirement. */
    }
  }, [projectId]);

  const project = projects.find((candidate) => candidate.id === projectId) ?? null;

  const canEditProject = useHasProjectPermission(projectId, "p.edit");
  const hasRepoGrant = useAuthStore((state) =>
    state.globalPermissions.has("p.project.set_repo_credentials"),
  );
  const hasPlaneGrant = useAuthStore((state) =>
    state.globalPermissions.has("p.project.set_plane_credentials"),
  );
  // Two ways in, and they see different halves: `u.view` administers the
  // personal environments of everybody, `p.env.use` is what a PM has to open
  // the shared ones. Gating the tab on the admin grant alone is what would hide
  // the `qa` card from the profile the feature exists for.
  const isEnvironmentAdmin = useAuthStore((state) => state.globalPermissions.has("u.view"));
  const canUseEnvironments = useHasProjectPermission(projectId, "p.env.use");
  const canSeeEnvironments = isEnvironmentAdmin || canUseEnvironments;
  // Exactly the pair the server demands to write it (`a.edit` +
  // `u.assign_permissions`), and not `a.view`: reading who the automation acts
  // as is of no use to somebody who cannot change it, and offering the tab
  // would promise a screen the backend refuses.
  const canManageQaAutomation = useAuthStore(
    (state) =>
      state.globalPermissions.has("a.edit") && state.globalPermissions.has("u.assign_permissions"),
  );

  const tabs = useMemo(() => {
    const entries: Array<{ readonly id: TabId; readonly label: string }> = [
      { id: "tags", label: "Etiquetas" },
    ];
    if (hasRepoGrant || canEditProject) entries.push({ id: "repo", label: "Repositorio" });
    if (hasPlaneGrant || canEditProject) entries.push({ id: "plane", label: "Plane" });
    if (canEditProject) entries.push({ id: "linked", label: "Vinculados" });
    // Ungated on purpose: the catalog is readable by anyone who can open the
    // project, and accepting a command on your own machine is nobody else's
    // decision. `LocalCommandsSection` hides the editing controls itself.
    entries.push({ id: "commands", label: "Comandos locales" });
    if (canSeeEnvironments) entries.push({ id: "environments", label: "Entornos" });
    if (canManageQaAutomation) entries.push({ id: "qa-automation", label: "Automatización QA" });
    return entries;
  }, [hasRepoGrant, hasPlaneGrant, canEditProject, canSeeEnvironments, canManageQaAutomation]);

  // A tab can disappear when the selection moves to a project the viewer has
  // fewer grants on; landing on a blank panel would look like a bug.
  useEffect(() => {
    if (!tabs.some((entry) => entry.id === tab)) setTab("tags");
  }, [tabs, tab]);

  if (projects.length === 0) {
    return (
      <div className="rounded-md border border-border/60 bg-card/40 p-6 text-sm text-muted-foreground">
        No hay proyectos todavía. Crea uno desde el sidebar y vuelve aquí para configurarlo.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="min-w-0">
        <h3 className="text-base font-semibold">Proyectos</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Todo lo que se administra de un proyecto: su repositorio, su tablero, con qué otros
          proyectos se vincula, qué puede ejecutar en tu máquina, quién tiene entorno permanente y
          qué es, en el vocabulario común de etiquetas.
        </p>
      </div>

      <Select
        value={projectId ?? ""}
        onValueChange={(value) => {
          if (!value) return;
          setProjectId(value as ProjectId);
        }}
      >
        <SelectTrigger className="w-full sm:w-96" aria-label="Proyecto">
          <SelectValue>{project?.name ?? "Elige un proyecto"}</SelectValue>
        </SelectTrigger>
        <SelectPopup align="start" alignItemWithTrigger={false}>
          {projects.map((candidate) => (
            <SelectItem hideIndicator key={candidate.id} value={candidate.id}>
              {candidate.name}
            </SelectItem>
          ))}
        </SelectPopup>
      </Select>

      {project !== null && (
        <p className="-mt-2 truncate font-mono text-[11px] text-muted-foreground/70">
          {project.cwd}
        </p>
      )}

      <div
        role="tablist"
        aria-label="Configuración del proyecto"
        className="flex flex-wrap gap-1 border-b border-border/60"
      >
        {tabs.map((entry) => (
          <button
            key={entry.id}
            type="button"
            role="tab"
            aria-selected={tab === entry.id}
            onClick={() => setTab(entry.id)}
            className={`-mb-px border-b-2 px-3 py-2 text-xs font-medium transition-colors ${
              tab === entry.id
                ? "border-brand text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {projectId !== null && project !== null && (
        // Keyed on the project so every tab starts from that project's server
        // state instead of inheriting the previous one's unsaved edits.
        <div key={projectId}>
          {tab === "tags" && <ProjectTagsTab projectId={projectId} />}
          {tab === "repo" && (
            <div className="flex flex-col gap-4">
              <ProjectBaseBranchSection projectId={projectId} />
              <ProjectRepoSection
                projectId={projectId}
                projectName={project.name}
                projectCwd={project.cwd}
                initialRepositoryUrl={project.repositoryUrl ?? null}
                initialStatus={project.repoCredentialStatus ?? "none"}
                initialError={project.repoCredentialError ?? null}
                validatedAt={project.repoCredentialValidatedAt ?? null}
              />
            </div>
          )}
          {tab === "plane" && <PlaneProjectSection projectId={projectId} />}
          {tab === "linked" && <LinkedProjectsSection projectId={projectId} />}
          {tab === "commands" && <LocalCommandsSection projectId={projectId} />}
          {tab === "environments" && <EnvironmentsSection projectId={projectId} />}
          {tab === "qa-automation" && <QaAutomationSection projectId={projectId} />}
        </div>
      )}
    </div>
  );
}

type LoadState = "idle" | "loading" | "saving" | "denied" | "error";

/**
 * The project's own tags.
 *
 * Separate from Ajustes → Etiquetas because the two answer different questions
 * — that screen decides which words exist, this one decides which of them
 * describe this repository — and mixing them taught the wrong model: that the
 * vocabulary and its use are one act.
 */
function ProjectTagsTab(props: { readonly projectId: ProjectId }) {
  const { projectId } = props;
  const [available, setAvailable] = useState<ReadonlyArray<Tag>>([]);
  const [tagIds, setTagIds] = useState<ReadonlyArray<TagId>>([]);
  const [status, setStatus] = useState<LoadState>("idle");

  useEffect(() => {
    // Failures are swallowed: someone with `p.edit` and no `t.view` still
    // belongs on this screen, they simply have nothing to pick from.
    void TagsClient.list()
      .then((entries) => setAvailable(entries.map((entry) => entry.tag)))
      .catch(() => setAvailable([]));
  }, []);

  useEffect(() => {
    setStatus("loading");
    void TagsClient.projectTags(projectId)
      .then((ids) => {
        setTagIds(ids);
        setStatus("idle");
      })
      .catch(() => {
        setTagIds([]);
        // Not an error worth a red banner — it means this project is not
        // theirs to edit, which is a perfectly ordinary answer.
        setStatus("denied");
      });
  }, [projectId]);

  const save = (next: ReadonlyArray<TagId>) => {
    setTagIds(next);
    setStatus("saving");
    void TagsClient.setProjectTags(projectId, next)
      .then(() => setStatus("idle"))
      .catch(() => setStatus("error"));
  };

  if (status === "denied") {
    return (
      <div className="rounded-md border border-border/60 bg-card/40 p-6 text-sm text-muted-foreground">
        Necesitas el permiso <code>p.edit</code> en este proyecto para ver o cambiar sus etiquetas.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-muted-foreground">
        Un agente que trabaje aquí carga, además de lo suyo, todo lo etiquetado con esto — por eso
        una memoria de <code>dominio:android</code> llega a cualquier agente en un proyecto Android,
        sea del rol que sea.
      </p>
      <TagPicker
        available={available}
        selected={tagIds}
        disabled={status === "loading"}
        onChange={save}
      />
      {status === "saving" && <span className="text-xs text-muted-foreground">Guardando…</span>}
      {status === "error" && (
        <p className="text-xs text-destructive">No se pudieron guardar las etiquetas.</p>
      )}
    </div>
  );
}
